<template>
  <section
    class="product-example-picker"
    aria-labelledby="product-picker-title"
  >
    <div class="product-picker-heading">
      <div>
        <span class="etq-panel-kicker">Dados reais do cliente</span>
        <h6 id="product-picker-title">Produto usado no teste</h6>
      </div>
      <button
        v-if="selectedProduct"
        type="button"
        class="btn btn-xs btn-light-danger font-weight-bold"
        @click="clearSelection"
      >
        Limpar
      </button>
    </div>

    <article v-if="selectedProduct" class="selected-product-card">
      <span class="product-icon"><i class="fas fa-box"></i></span>
      <div class="product-summary">
        <strong>{{ selectedProduct.descricao }}</strong>
        <small>{{ productIdentifiers(selectedProduct) }}</small>
        <b>{{ formatPrice(selectedProduct.preco) }}</b>
      </div>
      <button
        type="button"
        class="btn btn-xs btn-light-primary font-weight-bold"
        @click="clearSelection"
      >
        Trocar
      </button>
    </article>

    <template v-else>
      <div class="input-icon">
        <input
          v-model.trim="search"
          class="form-control form-control-sm"
          type="search"
          autocomplete="off"
          placeholder="Descrição, código, referência ou EAN"
          aria-label="Buscar produto real para o teste"
          @input="scheduleSearch"
        />
        <span>
          <i
            :class="
              loading
                ? 'spinner spinner-sm spinner-primary'
                : 'flaticon2-search-1 text-muted'
            "
          ></i>
        </span>
      </div>

      <div v-if="error" class="product-picker-state error">
        <i class="fas fa-exclamation-circle"></i>{{ error }}
      </div>
      <div
        v-else-if="search && !loading && !results.length"
        class="product-picker-state"
      >
        Nenhum produto ativo encontrado nesta empresa.
      </div>
      <div v-else-if="!search" class="product-picker-state">
        Digite para buscar entre os produtos ativos da empresa selecionada.
      </div>

      <div v-if="results.length" class="product-results">
        <button
          v-for="product in results"
          :key="product.id"
          type="button"
          :disabled="selectingId === product.id"
          @click="selectProduct(product)"
        >
          <span>
            <strong>{{ product.descricao }}</strong>
            <small>{{ productIdentifiers(product) }}</small>
          </span>
          <b>{{ formatPrice(product.preco) }}</b>
          <i
            :class="
              selectingId === product.id
                ? 'spinner spinner-sm spinner-primary'
                : 'fas fa-chevron-right'
            "
          ></i>
        </button>
      </div>
    </template>
  </section>
</template>

<script>
import etiquetasService from "@/modules/Etiquetas/services/etiquetasService";

export default {
  name: "ProductExamplePicker",
  props: {
    modelUuid: {
      type: String,
      required: true,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    selectedProduct: {
      type: Object,
      default: null,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      search: "",
      results: [],
      loading: false,
      selectingId: null,
      error: "",
      timer: null,
      requestId: 0,
    };
  },
  watch: {
    companyId() {
      this.resetSearch();
    },
  },
  beforeDestroy() {
    if (this.timer) clearTimeout(this.timer);
    this.requestId += 1;
  },
  methods: {
    scheduleSearch() {
      if (this.timer) clearTimeout(this.timer);
      this.requestId += 1;
      this.error = "";
      this.timer = setTimeout(() => {
        this.timer = null;
        this.searchProducts();
      }, 300);
    },
    async searchProducts() {
      const term = this.search.trim();
      const companyId = Number(this.companyId);
      const requestId = this.requestId;
      if (!term || !this.companyId) {
        this.results = [];
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = "";
      try {
        const { data } = await etiquetasService.buscarProdutosExemplo({
          modelo_uuid: this.modelUuid,
          id_empresa_exemplo: companyId,
          busca: term,
        });
        if (
          requestId !== this.requestId ||
          term !== this.search.trim() ||
          companyId !== Number(this.companyId)
        )
          return;
        this.results = (Array.isArray(data) ? data : []).slice(0, 30);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.results = [];
        this.error = "Não foi possível buscar os produtos. Tente novamente.";
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async selectProduct(product) {
      const requestId = ++this.requestId;
      const companyId = Number(this.companyId);
      this.selectingId = product.id;
      this.error = "";
      try {
        const { data } = await etiquetasService.produtoExemplo({
          modelo_uuid: this.modelUuid,
          id_empresa_exemplo: companyId,
          id_produto: Number(product.id),
          opcoes: this.options,
        });
        if (
          requestId !== this.requestId ||
          companyId !== Number(this.companyId)
        )
          return;
        this.$emit("select", {
          product: data.produto || product,
          values:
            data.dados_etiqueta && typeof data.dados_etiqueta === "object"
              ? data.dados_etiqueta
              : {},
        });
        this.search = "";
        this.results = [];
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.error = "Não foi possível carregar este produto para o teste.";
      } finally {
        if (requestId === this.requestId) this.selectingId = null;
      }
    },
    clearSelection() {
      this.resetSearch();
      this.$emit("clear");
    },
    resetSearch() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
      this.requestId += 1;
      this.search = "";
      this.results = [];
      this.error = "";
      this.loading = false;
      this.selectingId = null;
    },
    productIdentifiers(product) {
      return [
        product.codigo ? `Código ${product.codigo}` : "",
        product.referencia ? `Ref. ${product.referencia}` : "",
        product.cod_barras ? `EAN ${product.cod_barras}` : "",
      ]
        .filter(Boolean)
        .join(" · ");
    },
    formatPrice(value) {
      const price = Number(value);
      return Number.isFinite(price)
        ? price.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })
        : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.product-example-picker {
  margin: 14px 0;
  padding: 13px;
  border: 1px solid #dcecf4;
  border-radius: 10px;
  background: #f8fcff;
}

.product-picker-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.product-picker-heading h6 {
  margin: 3px 0 0;
  color: #25213b;
  font-size: 13px;
  font-weight: 800;
}

.selected-product-card {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px;
  border: 1px solid #cce8dc;
  border-radius: 8px;
  background: #f0fff8;
}

.product-icon {
  display: grid;
  flex: 0 0 30px;
  height: 30px;
  place-items: center;
  border-radius: 7px;
  background: #d5f5e6;
  color: #159466;
}

.product-summary,
.product-results button > span {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}

.product-summary strong,
.product-results strong {
  overflow: hidden;
  color: #38344c;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-summary small,
.product-results small {
  overflow: hidden;
  color: #868197;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-summary b,
.product-results b {
  margin-top: 2px;
  color: #159466;
  font-size: 10px;
}

.product-results {
  overflow: auto;
  max-height: 240px;
  margin-top: 7px;
  border: 1px solid #e4eaf0;
  border-radius: 7px;
  background: #fff;
}

.product-results button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 9px;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: transparent;
  text-align: left;
}

.product-results button:hover {
  background: #f4f9ff;
}

.product-results button:last-child {
  border-bottom: 0;
}

.product-picker-state {
  padding: 9px 3px 0;
  color: #8b879a;
  font-size: 10px;
  line-height: 1.4;
}

.product-picker-state.error {
  color: #f64e60;
}

.product-picker-state i {
  margin-right: 5px;
}
</style>
